import { createId, isoNow } from './crypto';
import { createStudyRecord } from './schema';
import type { IndexedDbStudyRepository } from './repository';
import type { StudyRecord } from './types';

const LEGACY_ROOT_KEY = 'nuclearStudy2026';
const MIGRATION_MARKER = 'migration:legacy-localStorage:v1';
const LEGACY_DATES = [
  '2026-08-14', '2026-08-15', '2026-08-16', '2026-08-17', '2026-08-18', '2026-08-19',
  '2026-08-20', '2026-08-21', '2026-08-22', '2026-08-23', '2026-08-24', '2026-08-25',
  '2026-08-26', '2026-08-27', '2026-08-28', '2026-08-29', '2026-08-30', '2026-08-31',
  '2026-09-01', '2026-09-02', '2026-09-03', '2026-09-04',
];

interface LegacyMigrationOptions {
  force?: boolean;
  removeLegacyAfterSuccess?: boolean;
}

export interface LegacyMigrationResult {
  migrated: boolean;
  records: number;
  settings: number;
  sourceKeys: string[];
  warnings: string[];
}

const asObject = (value: unknown): Record<string, unknown> | undefined =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? value as Record<string, unknown>
    : undefined;

function parseJson(value: string, key: string, warnings: string[]): unknown {
  try {
    return JSON.parse(value);
  } catch {
    warnings.push(`${key} 不是有效 JSON，已保留原文但未转换`);
    return undefined;
  }
}

export async function migrateLegacyLocalStorage(
  repository: IndexedDbStudyRepository,
  storage?: Storage,
  options: LegacyMigrationOptions = {},
): Promise<LegacyMigrationResult> {
  const previous = await repository.getSetting<string>(MIGRATION_MARKER);
  if (previous && !options.force) {
    return { migrated: false, records: 0, settings: 0, sourceKeys: [], warnings: [`已于 ${previous} 执行过迁移`] };
  }

  const deviceId = await repository.getDeviceId();
  const warnings: string[] = [];
  const sourceKeys: string[] = [];
  const raw: Record<string, string> = {};
  let source: Storage;
  try {
    source = storage ?? globalThis.localStorage;
  } catch {
    await repository.setSetting(MIGRATION_MARKER, isoNow());
    return { migrated: false, records: 0, settings: 0, sourceKeys, warnings: ['当前 origin 禁止访问 localStorage'] };
  }
  for (let index = 0; index < source.length; index += 1) {
    const key = source.key(index);
    if (!key || !key.startsWith(LEGACY_ROOT_KEY)) continue;
    const value = source.getItem(key);
    if (value === null) continue;
    sourceKeys.push(key);
    raw[key] = value;
  }
  if (!sourceKeys.length) {
    await repository.setSetting(MIGRATION_MARKER, isoNow());
    return { migrated: false, records: 0, settings: 0, sourceKeys, warnings: ['当前 origin 未发现旧版 localStorage 数据'] };
  }

  await repository.saveSnapshot({
    id: createId('snapshot'),
    reason: 'before-migration',
    createdAt: isoNow(),
    schemaVersion: 0,
    blob: new Blob([JSON.stringify(raw, null, 2)], { type: 'application/json' }),
  });

  const records: StudyRecord[] = [];
  let settings = 0;
  const rootRaw = raw[LEGACY_ROOT_KEY];
  if (rootRaw) {
    const root = asObject(parseJson(rootRaw, LEGACY_ROOT_KEY, warnings));
    if (root) {
      for (const [key, value] of Object.entries(root)) {
        const flatDay = /^day:(\d+)$/.exec(key);
        if (flatDay) {
          const dayIndex = Number(flatDay[1]);
          const studyDate = LEGACY_DATES[dayIndex];
          if (studyDate) records.push(createStudyRecord({
            id: studyDate,
            type: 'plan-day',
            deviceId,
            studyDate,
            source: 'legacy-localStorage',
            payload: { archived: Boolean(value), legacyDayIndex: dayIndex },
          }));
          continue;
        }
        if (key.startsWith('setup:')) {
          await repository.setSetting(key, Boolean(value));
          settings += 1;
        }
      }
      const nestedDays = asObject(root.days);
      if (nestedDays) {
        for (const [key, value] of Object.entries(nestedDays)) {
          const numericIndex = Number(key);
          const studyDate = /^\d{4}-\d{2}-\d{2}$/.test(key) ? key : LEGACY_DATES[numericIndex];
          if (!studyDate) continue;
          records.push(createStudyRecord({
            id: studyDate,
            type: 'plan-day',
            deviceId,
            studyDate,
            source: 'legacy-localStorage',
            payload: { archived: Boolean(value), legacyDayIndex: Number.isFinite(numericIndex) ? numericIndex : undefined },
          }));
        }
      }
      const nestedSetup = asObject(root.setup);
      if (nestedSetup) {
        for (const [key, value] of Object.entries(nestedSetup)) {
          await repository.setSetting(`setup:${key}`, Boolean(value));
          settings += 1;
        }
      }
    }
  }

  for (const [key, value] of Object.entries(raw)) {
    const dailyMatch = /^nuclearStudy2026:(\d{4}-\d{2}-\d{2})$/.exec(key);
    if (dailyMatch) {
      const studyDate = dailyMatch[1];
      const tasks = asObject(parseJson(value, key, warnings));
      if (!tasks) continue;
      for (const [taskId, completed] of Object.entries(tasks)) {
        records.push(createStudyRecord({
          id: `${studyDate}:${taskId}`,
          type: 'task-completion',
          deviceId,
          studyDate,
          source: 'legacy-localStorage',
          payload: { taskId, completed: Boolean(completed), migratedFrom: key },
        }));
      }
      continue;
    }
    const feedbackMatch = /^nuclearStudy2026:(\d{4}-\d{2}-\d{2}):feedback$/.exec(key);
    if (feedbackMatch) {
      const studyDate = feedbackMatch[1];
      records.push(createStudyRecord({
        id: `${studyDate}:legacy-draft`,
        type: 'daily-report',
        deviceId,
        studyDate,
        source: 'legacy-localStorage',
        payload: { status: 'draft', legacyFeedback: value },
      }));
    }
  }

  const uniqueRecords = new Map(records.map((record) => [record.key, record]));
  for (const record of uniqueRecords.values()) await repository.saveRecord(record);
  await repository.setSetting(MIGRATION_MARKER, isoNow());
  await repository.setSetting(`migration:legacy-raw:${isoNow()}`, raw);

  if (options.removeLegacyAfterSuccess) sourceKeys.forEach((key) => source.removeItem(key));

  return {
    migrated: true,
    records: uniqueRecords.size,
    settings,
    sourceKeys,
    warnings,
  };
}
